import {
  Loader2,
  Calendar as CalendarIcon,
  ChevronLeft,
  ChevronRight,
} from "lucide-react";
import { DoctorsList } from "./DoctorsList";
import doctorIllustration from "../../assets/images/doctor-placeholder.png";
import {
  AppointmentSetting,
  AppointmentSettings,
  DoctorDataResponse,
  DoctorDetails,
  DoctorWithSettingsResponse,
  Slot,
} from "./types";
import React, { useEffect, useState } from "react";
import { AppointmentService } from "../../services/api/appointment.service";
import toast from "react-hot-toast";

interface FindDoctorBoxProps {
  isSearchingDoctors: boolean;
  selectedDoctor: DoctorDetails | null;
  isAppointmentBooked: boolean;
  selectedTimeSlot: Slot | null;
  setSelectedDoctor: (doctor: DoctorDetails) => void;
  setSelectedTimeSlot: (timeSlot: Slot | null) => void;
  handleBookAppointment: (e: React.MouseEvent) => void;
  handleDateFilterOnChange: (date: string) => void;
  dateFilter: string | null;
  handleFindDoctors: () => void;
  isVerified: boolean;
  doctorListing: DoctorDataResponse; // Keeping for backward compatibility if needed, but we will use internal state for new API
  selectedAppointmentSetting: AppointmentSettings | null;
  setSelectedAppointmentSetting: React.Dispatch<
    React.SetStateAction<AppointmentSettings | null>
  >;
}

function FindDoctorBox({
  selectedDoctor,
  isAppointmentBooked,
  selectedTimeSlot,
  setSelectedDoctor,
  setSelectedTimeSlot,
  handleBookAppointment,
  handleDateFilterOnChange,
  dateFilter,
  isVerified,
  selectedAppointmentSetting,
  setSelectedAppointmentSetting,
}: FindDoctorBoxProps) {
  const [loading, setLoading] = useState(false);
  const [doctors, setDoctors] = useState<DoctorWithSettingsResponse["data"]>(
    [],
  );
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 1, // As per user request "limit=1" initially, but typically more. Let's stick to 5 or 10 for UI. User said limit=1 in example request but likely meant page 1. Assuming 5 for better UX. Actually user request has `limit=1` in the example URL. I will use 5 for better reading.
    total: 0,
    totalPages: 0,
  });
  const [showCalendar, setShowCalendar] = useState(false);

  // Initialize date if not present
  useEffect(() => {
    if (!dateFilter) {
      const today = new Date().toISOString().split("T")[0];
      handleDateFilterOnChange(today);
    }
  }, [dateFilter, handleDateFilterOnChange]);

  const fetchDoctors = async (date: string, page: number) => {
    setLoading(true);
    try {
      // User example showed limit=1, but for a list we usually want more. I'll default to 5.
      const response = await AppointmentService.getDoctorsWithSettings(
        date,
        page,
        5,
      );
      if (response.success && response.data) {
        setDoctors(response.data);
        setPagination((prev) => ({
          ...prev,
          page: response.pagination.page,
          total: response.pagination.total,
          totalPages: response.pagination.totalPages,
          limit: response.pagination.limit,
        }));
      } else {
        setDoctors([]);
        toast.error(response.message || "Failed to fetch doctors");
      }
    } catch (error) {
      console.error(error);
      toast.error("Something went wrong");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isVerified && dateFilter) {
      fetchDoctors(dateFilter, 1);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isVerified]);

  const handlePageChange = (newPage: number) => {
    if (newPage >= 1 && newPage <= pagination.totalPages && dateFilter) {
      fetchDoctors(dateFilter, newPage);
    }
  };

  // Custom Calendar Implementation
  const CustomDatePicker = () => {
  const today = new Date();
  today.setHours(0, 0, 0, 0); // normalize once

  const [currentMonth, setCurrentMonth] = useState(
    new Date(dateFilter || today)
  );

  // 👉 Generate calendar with proper alignment
  const getCalendarDays = (date: Date) => {
    const year = date.getFullYear();
    const month = date.getMonth();

    const firstDayOfMonth = new Date(year, month, 1).getDay(); // 0–6
    const totalDays = new Date(year, month + 1, 0).getDate();

    const days: (Date | null)[] = [];

    // ✅ Empty slots before first day
    for (let i = 0; i < firstDayOfMonth; i++) {
      days.push(null);
    }

    // ✅ Actual days
    for (let i = 1; i <= totalDays; i++) {
      days.push(new Date(year, month, i));
    }

    return days;
  };

  const days = getCalendarDays(currentMonth);

  const isSameDate = (d1: Date, stringDate: string | null) => {
    if (!stringDate) return false;
    const d2 = new Date(stringDate);
    return (
      d1.getDate() === d2.getDate() &&
      d1.getMonth() === d2.getMonth() &&
      d1.getFullYear() === d2.getFullYear()
    );
  };

  const handleDateClick = (date: Date) => {
    const dateString = `${date.getFullYear()}-${String(
      date.getMonth() + 1
    ).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;

    handleDateFilterOnChange(dateString);
    setShowCalendar(false);
  };

  const nextMonth = () => {
    setCurrentMonth(
      new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 1)
    );
  };

  const prevMonth = () => {
    setCurrentMonth(
      new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1, 1)
    );
  };

  return (
    <div className="absolute top-12 right-0 z-50 bg-white shadow-xl border rounded-lg p-4 w-72">
      {/* Header */}
      <div className="flex justify-between items-center mb-4">
        <button
          onClick={prevMonth}
          className="p-1 hover:bg-gray-100 rounded-full"
        >
          <ChevronLeft className="w-5 h-5" />
        </button>

        <span className="font-semibold">
          {currentMonth.toLocaleString("default", {
            month: "long",
            year: "numeric",
          })}
        </span>

        <button
          onClick={nextMonth}
          className="p-1 hover:bg-gray-100 rounded-full"
        >
          <ChevronRight className="w-5 h-5" />
        </button>
      </div>

      {/* Week Days */}
      <div className="grid grid-cols-7 gap-1 text-center text-xs">
        {["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"].map((d) => (
          <div key={d} className="font-bold text-gray-500 py-1">
            {d}
          </div>
        ))}

        {/* Dates */}
        {days.map((date, i) => {
          if (!date) {
            return <div key={i}></div>; // empty slot
          }

          const isPast = date < today;

          return (
            <button
              key={i}
              onClick={() => handleDateClick(date)}
              disabled={isPast}
              className={`p-2 rounded-full transition
                ${
                  isSameDate(date, dateFilter)
                    ? "bg-emerald-600 text-white hover:bg-emerald-700"
                    : "hover:bg-emerald-50"
                }
                ${isPast ? "text-gray-300 cursor-not-allowed" : ""}
              `}
            >
              {date.getDate()}
            </button>
          );
        })}
      </div>
    </div>
  );
};

  return (
    <>
      <div
        className={`mb-4 w-full py-2 flex items-center justify-between border-y border-gray-400/40 ${isVerified ? "flex" : "hidden"}`}
      >
        <h3 className="text-sm font-medium text-gray-900">
          Available Doctors ({pagination.total})
        </h3>
        <div className="flex items-center gap-3">
          <div className="relative">
            <button
              onClick={() => setShowCalendar(!showCalendar)}
              className="flex items-center gap-2 border px-3 py-1.5 rounded-md text-sm text-gray-600 hover:border-emerald-500 hover:text-emerald-600 transition-colors"
            >
              <CalendarIcon className="w-4 h-4" />
              {dateFilter || "Select Date"}
            </button>
            {showCalendar && <CustomDatePicker />}
          </div>
          <button
            onClick={() =>
              fetchDoctors(
                dateFilter || new Date().toISOString().split("T")[0],
                1,
              )
            }
            className="px-3 py-1.5 bg-emerald-600 text-white text-sm rounded-md hover:bg-emerald-700 transition-colors"
          >
            Find Doctors
          </button>
        </div>
      </div>

      {loading ? (
        <div className="flex-1 flex flex-col items-center justify-center">
          <Loader2 className="w-8 h-8 text-emerald-600 animate-spin mb-4" />
          <p className="text-gray-600">
            Finding available doctors in your area...
          </p>
        </div>
      ) : doctors.length > 0 ? (
        <div className="w-full">
          <DoctorsList
            selectedDoctor={selectedDoctor}
            onCompleteAppointment={isAppointmentBooked}
            selectedTimeSlot={selectedTimeSlot}
            setSelectedDoctor={setSelectedDoctor}
            setSelectedTimeSlot={setSelectedTimeSlot}
            onBookAppointment={handleBookAppointment}
            onDateChange={handleDateFilterOnChange}
            dateFilter={dateFilter}
            // Transform new data structure to match what DoctorsList (and children) might expect or update DoctorsList to handle this new structure.
            // For now, I'll pass the new doctors array and update DoctorsList to handle it.
            doctorsList={doctors}
            selectedAppointmentSetting={selectedAppointmentSetting}
            setSelectedAppointmentSetting={setSelectedAppointmentSetting}
          />

          {/* Pagination Controls */}
          {pagination.totalPages > 1 && (
            <div className="flex justify-center items-center gap-4 mt-6">
              <button
                onClick={() => handlePageChange(pagination.page - 1)}
                disabled={pagination.page === 1}
                className="p-2 border rounded-md disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
              >
                <ChevronLeft className="w-4 h-4" />
              </button>
              <span className="text-sm text-gray-600">
                Page {pagination.page} of {pagination.totalPages}
              </span>
              <button
                onClick={() => handlePageChange(pagination.page + 1)}
                disabled={pagination.page === pagination.totalPages}
                className="p-2 border rounded-md disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
              >
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          )}
        </div>
      ) : (
        <div className="flex-1 flex flex-col items-center justify-center w-full">
          <img
            src={doctorIllustration}
            alt="Doctor Illustration"
            className="w-auto h-64 mb-6 opacity-80"
          />
          <p className="text-center text-gray-600 mb-6">
            {isVerified
              ? "No doctors found for this date."
              : "Update patient illness details and then search for doctors."}
          </p>
        </div>
      )}
    </>
  );
}

export default FindDoctorBox;
